import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { getSession } from '../../getSession';
import { getPipelines, getPipelineStages, getUserFromHashedList } from '../../utils/lead';

const DEFAULT_STAGES = ['New', 'Contacted', 'Proposal', 'Welcome Mail send', 'Negotiation', 'Won', 'Lost'];
const OWNER_FALLBACK = [
  { value: '34594-Mohit.cheema', label: '34594-Mohit.cheema (Mohit Cheema)' },
  { value: '34594-Manish.Singh', label: '34594-Manish.Singh (Manish Singh)' },
  { value: '34594-kmukesh343', label: '34594-kmukesh343 (Mukesh Kumar)' }
];

const AddDeal = forwardRef(({ onSubmit, onClose, initial = {} }, ref) => {
  const [dealValue, setDealValue] = useState(initial.dealValue || '');
  const [pipelines, setPipelines] = useState([]); // array of {value,label}
  const [pipeline, setPipeline] = useState(initial.pipeline || '');
  const [stages, setStages] = useState(DEFAULT_STAGES); // array of {value,label} or fallback strings
  const [stage, setStage] = useState(initial.stage || DEFAULT_STAGES[0]);
  const [probability, setProbability] = useState(initial.probability || '5.00');
  const [owners, setOwners] = useState(OWNER_FALLBACK);
  const [salesOwner, setSalesOwner] = useState(initial.salesOwner || (OWNER_FALLBACK[0] && OWNER_FALLBACK[0].value));
  const todayStr = new Date().toISOString().split('T')[0];
  const [estimatedCloserDate, setEstimatedCloserDate] = useState(initial.estimatedCloserDate || todayStr);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!dealValue || String(dealValue).trim() === '') e.dealValue = 'Please enter deal value';
    if (!probability || isNaN(Number(probability))) e.probability = 'Enter valid probability';
    
    const today = new Date().toISOString().split('T')[0];
    if (estimatedCloserDate && estimatedCloserDate < today) {
      if (initial.estimatedCloserDate && estimatedCloserDate === initial.estimatedCloserDate) {
         // Allow preserving existing past date if editing an old deal
      } else {
         e.estimatedCloserDate = 'Estimated closer date cannot be in the past';
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const payload = {
      dealValue,
      pipeline,
      stage,
      probability: Number(probability),
      salesOwner,
      estimatedCloserDate
    };
    if (onSubmit) onSubmit(payload);
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { userId, TokenId, parentId } = getSession();
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { ParentID: parentId || 0, PipelineId: pipeline },
          BroadcastName: ''
        };
        const resp = await getPipelines(payload);
        const list = Array.isArray(resp) ? resp : [];
        const mapped = list.map(item => ({
          value: String(item?.PipeLineID ?? item?.PipelineId ?? item?.Value ?? item?.Code ?? item?.ValueId ?? ''),
          label: String(item?.Name ?? item?.PipelineName ?? item?.Text ?? item?.ProductName ?? item?.Value ?? item?.Code ?? item?.Id ?? item)
        }));
        if (mounted) {
          setPipelines(mapped);
          if ((!pipeline || pipeline === '') && mapped.length) setPipeline(initial.pipeline || mapped[0].value);
        }
      } catch (err) {
        console.warn('load pipelines error', err);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useImperativeHandle(ref, () => ({
    getFormData: () => {
      // return structured data expected by backend
      return {
        LeadId: initial.leadId || 0,
        PipelineId: pipeline ? Number(pipeline) : 0,
        PipelineStage: stage ? Number(stage) : 0,
        Amount: dealValue ? Number(String(dealValue).replace(/,/g, '')) : 0,
        ClosureDate: estimatedCloserDate || '',
        DealOwner: salesOwner ? Number(String(salesOwner).replace(/[^0-9]/g, '')) : (salesOwner ? Number(salesOwner) : 0),
        Probability: probability ? Number(probability) : 0,
        UserId: getSession().userId || 0
      };
    }
  }));

  useEffect(() => {
    let mounted = true;
    const loadUsers = async () => {
      try {
        const { userId, TokenId } = getSession();
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { UserId: userId },
          BroadcastName: ''
        };
        const resp = await getUserFromHashedList(payload);
        let raw = resp?.Details ?? resp?.d ?? resp ?? [];
        if (raw && raw.data) raw = raw.data;
        if (raw && !Array.isArray(raw)) {
          raw = raw.ResultList ?? raw.Users ?? raw.UserList ?? raw.Data ?? raw.Items ?? raw;
        }
        const usersArray = Array.isArray(raw) ? raw : [];
        const mapped = usersArray.map(u => {
          const id = u.Code ?? u.Id ?? u.ID ?? u.UserId ?? u.Value ?? u.ValueId ?? '';
          const label = u.Text ?? u.TextN ?? u.Name ?? u.FullName ?? u.DisplayName ?? u.LoginName ?? String(id);
          return { value: String(id), label: String(label) };
        });
        if (mounted && mapped.length) {
          setOwners(mapped);
          if (!salesOwner) setSalesOwner(mapped[0].value);
        }
      } catch (err) {
        console.warn('load users error', err);
      }
    };
    loadUsers();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadStages = async () => {
        try {
            const { userId, TokenId, parentId } = getSession();

            const payload = {
                Token: TokenId,
                Message: '',
                LoggedUserId: userId,
                MAC_Address: '',
                IP_Address: '',
                Details: { 
                    ParentID: parentId || 0,
                    PipelineId: pipeline || 0   // ✅ PASS SELECTED PIPELINE
                },
                BroadcastName: ''
            };

            const resp = await getPipelineStages(payload);

            const list = Array.isArray(resp) ? resp : [];

            const mapped = list.map(item => ({
                value: String(item?.StageID ?? item?.StageId ?? ''),
                label: String(item?.StageName ?? item?.Name ?? '')
            }));

            if (mounted) {
                setStages(mapped.length ? mapped : DEFAULT_STAGES);

                // auto select first stage when pipeline changes
                if (mapped.length) {
                    setStage(mapped[0].value);
                }
            }
        } catch (err) {
            console.warn('load pipeline stages error', err);
        }
    };

    if (pipeline) {
        loadStages();   // ✅ call only when pipeline selected
    }

    return () => { mounted = false; };

}, [pipeline]); // ✅ IMPORTANT

  return (
    <div className="w-full max-h-[80vh] overflow-y-auto p-4">
      <form onSubmit={handleSave}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Deal value <span className="text-red-500">*</span></label>
          <input
            type="number"
            value={dealValue}
            min="0"
            step="0.01"
            onInput={e => {
              // Only allow numbers and a single dot
              let val = e.target.value.replace(/[^0-9.]/g, '');
              // Prevent multiple dots
              const parts = val.split('.');
              if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
              setDealValue(val);
            }}
            onChange={e => setDealValue(e.target.value)}
            placeholder="Please Enter Value"
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
          />
          {errors.dealValue && <div className="text-sm text-red-600 mt-1">{errors.dealValue}</div>}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Deal pipeline</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm" value={pipeline || ''} onChange={e => {setPipeline(String(e.target.value));setStage('');}}>
            <option value="">Select pipeline</option>
            {pipelines && pipelines.length ? pipelines.map(p => (
              <option key={p.value} value={String(p.value)}>{p.label}</option>
            )) : (
              <option value="">No pipelines</option>
            )}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Deal Stage</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm" value={stage} onChange={e => setStage(e.target.value)}>
            {Array.isArray(stages) && stages.length && typeof stages[0] === 'object' ? (
              stages.map(s => <option key={s.value} value={s.value}>{s.label}</option>)
            ) : (
              stages.map(s => <option key={s} value={s}>{s}</option>)
            )}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Probability(%)<span className="text-red-500">*</span></label>
          <input
            type="number"
            step="0.01"
            value={probability}
            onChange={e => setProbability(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
          />
          {errors.probability && <div className="text-sm text-red-600 mt-1">{errors.probability}</div>}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Sales owner</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50" value={salesOwner} onChange={e => setSalesOwner(e.target.value)}>
            {owners && owners.length ? owners.map(o => <option key={o.value} value={o.value}>{o.label}</option>) : <option value="">No users</option>}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Estimated Closer Date</label>
          <input
            type="date"
            min={new Date().toISOString().split('T')[0]}
            value={estimatedCloserDate}
            onChange={e => {
              const val = e.target.value;
              const today = new Date().toISOString().split('T')[0];
              if (val && val < today) {
                setEstimatedCloserDate(today);
                setErrors(prev => ({...prev, estimatedCloserDate: 'Cannot select a past date'}));
              } else {
                setEstimatedCloserDate(val);
                setErrors(prev => ({...prev, estimatedCloserDate: null}));
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
          />
          {errors.estimatedCloserDate && <div className="text-sm text-red-600 mt-1">{errors.estimatedCloserDate}</div>}
        </div>

      </form>
    </div>
  );
});

export default AddDeal;